import {useCallback, useEffect, useMemo, useRef} from 'react';

import {pathOf, searchOf, useAppRouter} from '../../lib/routes';
import {useToggle} from '../utils/hooks';
import {FilterSchema, FilterValues, parseFilters, serializeFilters} from './filter-params';

export type Filters<S extends FilterSchema> = {
	values: FilterValues<S>;
	/** выбрано хоть одно значение */
	isSet: boolean;
	open: boolean;
	toggle: () => void;
	set<K extends keyof S>(key: K, value: FilterValues<S>[K]): void;
	reset(): void;
	/** пропсы для группы кнопок */
	field<K extends keyof S>(
		key: K,
	): {
		value: FilterValues<S>[K];
		onChange: (value: FilterValues<S>[K] | null) => void;
	};
	/** пропсы для флажка */
	flag<K extends keyof S>(key: K): {checked: boolean; onChange: (value: boolean) => void};
};

export function useFilters<S extends FilterSchema>(schema: S): Filters<S> {
	const router = useAppRouter();
	const asPath = router?.asPath || '';
	const search = searchOf(asPath);
	const [open, toggle] = useToggle(false);

	const values = useMemo(() => parseFilters(schema, new URLSearchParams(search)), [schema, search]);

	const latest = useRef(values);
	useEffect(() => {
		latest.current = values;
	}, [values]);

	const write = useCallback(
		(next: FilterValues<S>) => {
			latest.current = next;
			const params = serializeFilters(
				schema,
				next,
				new URLSearchParams(searchOf(router?.asPath || '')),
			).toString();
			const path = pathOf(router?.asPath || '');
			router?.replace(params ? `${path}?${params}` : path, undefined, {shallow: true, scroll: false});
		},
		[schema, router],
	);

	const set = useCallback(
		<K extends keyof S>(key: K, value: FilterValues<S>[K]) => write({...latest.current, [key]: value}),
		[write],
	);

	return {
		values,
		isSet: Object.keys(values).length > 0,
		open,
		toggle,
		set,
		reset: () => write({}),
		field: (key) => ({value: values[key], onChange: (value) => set(key, value ?? undefined)}),
		flag: (key) => ({checked: !!values[key], onChange: (value) => set(key, value as never)}),
	};
}
